import React, { useRef, useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import api from '../api/client';

class MyUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  upload() {
    return this.loader.file.then(
      file =>
        new Promise((resolve, reject) => {
          const data = new FormData();
          data.append('upload', file);

          api.post('/email-templates/upload_media/', data, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
            .then(res => {
              resolve({
                default: res.data.default || res.data.url
              });
            })
            .catch(err => {
              reject(err.response?.data?.error?.message || 'Could not upload file.');
            });
        })
    );
  }

  abort() {
    // optional abort logic
  }
}

function CustomUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new MyUploadAdapter(loader);
  };
}

export default function RichTextEditor({ value = '', onChange, readOnly = false, load = false }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && load) {
      const editable = editorRef.current.querySelector('.ck-editor__editable');
      if (editable) {
        editable.style.position = 'relative';
      }
    }
  }, [load]);

  return (
    <Box className="ckeditor-wrapper" ref={editorRef}>
      <CKEditor
        editor={ClassicEditor}
        data={value}
        disabled={readOnly || load}
        config={{
          extraPlugins: [CustomUploadAdapterPlugin],
          toolbar: [
            'heading', '|',
            'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', '|',
            'imageUpload', 'mediaEmbed', 'insertTable', 'undo', 'redo'
          ]
        }}
        onReady={(editor) => {
          editorRef.current = editor.ui.view.editable.element.closest('.ckeditor-wrapper');
        }}
        onChange={(event, editor) => {
          const data = editor.getData();
          onChange(data);
        }}
      />

      {/* Loader inside editable area */}
      {load && (
        <Box
          className="editor-loader"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2,
            borderRadius: '8px'
          }}
        >
          <CircularProgress size={20} /><span style={{ marginLeft: '5px', color: '#227bfd' }}>Submitting your answer, please wait...</span>
        </Box>
      )}

      <style>{`
        .ckeditor-wrapper {
          position: relative;
          border: 1.5px solid #e3e8f0;
          border-radius: 12px;
          background: white;
          font-size: 1rem;
          color: #1e293b;
          min-height: auto;
          overflow: hidden;
        }

        .ck-editor__editable_inline {
          min-height: 400px;
          max-height: calc(100vh - 500px) !important; /* Responsive height for scroll */
          overflow-y: auto !important;
          font-family: 'Inter', 'Poppins', 'Roboto', Arial, Helvetica, sans-serif;
          position: relative;
        }
      `}</style>
    </Box>
  );
}
